(function () {
  if (customElements.get('discount-progress-bar')) return;

  // Fixed visual positions for the two tier stops along the track.
  // These stay constant regardless of the actual threshold values so the
  // bar always reads cleanly - only the fill amount + active states move.
  var TIER1_POS = 30; // %
  var TIER2_POS = 85; // %

  // The free "A LITTLE SURPRISE" gift (same variant as assets/cart.js GIFT_VARIANT_ID)
  // and the gift-wrap add-on are never counted toward a Buy More Save More tier.
  var GIFT_VARIANT_ID = 48707675029781;

  function norm(v) {
    return String(v == null ? '' : v).toLowerCase();
  }

  // "Bras" -> "bra", "Panties" -> "panty": so item_label works as a fallback keyword.
  function singular(w) {
    w = norm(w).trim();
    if (/ies$/.test(w)) return w.replace(/ies$/, 'y');
    if (/s$/.test(w) && !/ss$/.test(w)) return w.replace(/s$/, '');
    return w;
  }

  function isExcludedLine(item) {
    if (!item) return true;
    if (Number(item.variant_id) === GIFT_VARIANT_ID) return true;
    var hay = norm(item.product_title) + ' ' + norm(item.title) + ' ' + norm(item.handle);
    if (hay.indexOf('surprise') !== -1) return true;
    if (hay.indexOf('gift-wrap') !== -1 || hay.indexOf('gift wrap') !== -1 || hay.indexOf('gift-wrapping') !== -1) return true;
    // Free / zero-priced promo lines never count either.
    if (Number(item.final_line_price) === 0 && Number(item.original_line_price) === 0) return true;
    return false;
  }

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
      return r.json();
    });
  }

  class DiscountProgressBarElement extends HTMLElement {
    connectedCallback() {
      this.tier1Qty = parseFloat(this.dataset.tier1Qty) || 0;
      this.tier2Qty = parseFloat(this.dataset.tier2Qty) || 0;

      // What this bar counts: items whose product type (or title/handle) matches one of
      // these keywords - e.g. only bras on a bra page, only panties on a panty page.
      var kw = (this.dataset.countKeywords || '').split(',');
      this.keywords = kw.map(function (k) { return norm(k).trim(); }).filter(Boolean);
      this.productType = norm(this.dataset.productType).trim();
      if (!this.keywords.length && !this.productType) {
        var fallback = singular(this.dataset.itemLabel || '');
        if (fallback) this.keywords = [fallback];
      }

      this.fillEl = this.querySelector('[data-dp-fill]');
      this.dot1 = this.querySelector('[data-dp-dot="1"]');
      this.dot2 = this.querySelector('[data-dp-dot="2"]');
      this.label1 = this.querySelector('[data-dp-label="1"]');
      this.label2 = this.querySelector('[data-dp-label="2"]');
      this.threshold1 = this.querySelector('[data-dp-threshold="1"]');
      this.threshold2 = this.querySelector('[data-dp-threshold="2"]');

      this.style.setProperty('--dp-tier1-pos', TIER1_POS + '%');
      this.style.setProperty('--dp-tier2-pos', TIER2_POS + '%');

      this._onCartChange = this.refresh.bind(this);

      // This theme (Minimog) broadcasts cart changes - add to cart, quantity
      // +/- in the cart drawer, and line removal - through its own pub/sub
      // helper rather than DOM CustomEvents, so we hook into that directly.
      // This is what makes the bar move live as items are added/removed.
      //
      // window.MinimogEvents is defined by theme-global.js. This element's
      // own script is loaded with `defer` so it normally runs after
      // theme-global.js, but if anything ever changes that load order again,
      // fail safe by polling briefly instead of silently never subscribing
      // (which is what happened before - the bar would only ever show the
      // right count after a full page reload).
      this._subscribeToCartUpdates();

      // Kept as a fallback in case any other app/script on the store dispatches
      // these more generic DOM events.
      document.addEventListener('cart:updated', this._onCartChange);
      document.addEventListener('ajaxProduct:added', this._onCartChange);
      document.addEventListener('cart:build', this._onCartChange);

      this.refresh();
    }

    _subscribeToCartUpdates(attemptsLeft) {
      if (attemptsLeft === undefined) attemptsLeft = 40; // ~4s at 100ms, generous vs. any slow app script

      if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
        this._cartUpdateUnsubscribe = window.MinimogEvents.subscribe(
          window.MinimogTheme.pubSubEvents.cartUpdate,
          this._onCartChange
        );
        return;
      }

      if (attemptsLeft <= 0) return;
      var self = this;
      this._subscribeRetryTimeout = setTimeout(function () {
        self._subscribeToCartUpdates(attemptsLeft - 1);
      }, 100);
    }

    disconnectedCallback() {
      if (this._subscribeRetryTimeout) clearTimeout(this._subscribeRetryTimeout);
      if (typeof this._cartUpdateUnsubscribe === 'function') {
        this._cartUpdateUnsubscribe();
      }
      document.removeEventListener('cart:updated', this._onCartChange);
      document.removeEventListener('ajaxProduct:added', this._onCartChange);
      document.removeEventListener('cart:build', this._onCartChange);
    }

    refresh() {
      var self = this;
      getCart()
        .then(function (cart) {
          self.render(self.countMatchingItems(cart));
        })
        .catch(function () {
          self.render(0);
        });
    }

    matchesItem(item) {
      if (isExcludedLine(item)) return false;
      var type = norm(item.product_type).trim();

      // 1) Keywords set on the block (comma separated) win.
      if (this.keywords.length) {
        var hay = type + ' ' + norm(item.product_title) + ' ' + norm(item.handle);
        return this.keywords.some(function (k) { return hay.indexOf(k) !== -1; });
      }
      // 2) Otherwise: same product type as the product on this page.
      if (this.productType) return type === this.productType;
      return true;
    }

    countMatchingItems(cart) {
      var self = this;
      var items = (cart && cart.items) || [];
      return items.reduce(function (sum, item) {
        return self.matchesItem(item) ? sum + (Number(item.quantity) || 0) : sum;
      }, 0);
    }

    render(cartQty) {
      var t1 = this.tier1Qty;
      var t2 = this.tier2Qty;
      var fill = 0;

      if (t2 > 0) {
        if (cartQty <= 0) {
          fill = 0;
        } else if (t1 > 0 && cartQty < t1) {
          fill = (cartQty / t1) * TIER1_POS;
        } else if (cartQty < t2) {
          fill = TIER1_POS + ((cartQty - t1) / (t2 - t1)) * (TIER2_POS - TIER1_POS);
        } else {
          fill = 100;
        }
      }

      if (this.fillEl) this.fillEl.style.width = fill + '%';

      var reached1 = cartQty >= t1 && t1 > 0;
      var reached2 = cartQty >= t2 && t2 > 0;

      this.classList.toggle('discount-progress--tier1-reached', reached1);
      this.classList.toggle('discount-progress--tier2-reached', reached2);

      [this.dot1, this.label1, this.threshold1].forEach(function (el) {
        if (el) el.classList.toggle('is-active', reached1);
      });
      [this.dot2, this.label2, this.threshold2].forEach(function (el) {
        if (el) el.classList.toggle('is-active', reached2);
      });
    }
  }

  customElements.define('discount-progress-bar', DiscountProgressBarElement);
})();